import type { SVGProps } from 'react'

export type FlipbookIconName =
  | 'align-center'
  | 'align-left'
  | 'align-right'
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'arrow-up'
  | 'arrange-back'
  | 'arrange-down'
  | 'arrange-front'
  | 'arrange-up'
  | 'brush'
  | 'brush-3'
  | 'eraser'
  | 'maximize'
  | 'minimize'
  | 'mouse'
  | 'note'
  | 'redo'
  | 'search-zoom-in'
  | 'search-zoom-out'
  | 'text'
  | 'trash'
  | 'undo'

type FlipbookIconProps = SVGProps<SVGSVGElement> & {
  readonly name: FlipbookIconName
}

const iconStrokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  strokeWidth: 1.5,
}

export function FlipbookIcon({ name, ...props }: FlipbookIconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...iconStrokeProps} {...props}>
      {renderIcon(name)}
    </svg>
  )
}

function renderIcon(name: FlipbookIconName) {
  switch (name) {
    case 'align-center':
      return <><path d="M5 4.5H19" /><path d="M3 9.5H21" /><path d="M5 14.5H19" /><path d="M3 19.5H21" /></>
    case 'align-left':
      return <><path d="M5.1 19.25H16.9C18.4 19.25 19 18.61 19 17.02V15.98C19 14.39 18.4 13.75 16.9 13.75H5.1" /><path d="M5.1 5.25H11.9C13.4 5.25 14 5.89 14 7.48V8.52C14 10.11 13.4 10.75 11.9 10.75H5.1" /><path d="M5 1.99V21.99" /></>
    case 'align-right':
      return <g transform="translate(24 0) scale(-1 1)">{renderIcon('align-left')}</g>
    case 'arrow-up':
      return <><path d="M12 20.5V3.67" /><path d="M18.07 9.57L12 3.5L5.93 9.57" /></>
    case 'arrow-down':
      return <g transform="rotate(180 12 12)">{renderIcon('arrow-up')}</g>
    case 'arrow-left':
      return <><path d="M9.57 5.93L3.5 12L9.57 18.07" /><path d="M20.5 12H3.67" /></>
    case 'arrow-right':
      return <><path d="M14.43 5.93L20.5 12L14.43 18.07" /><path d="M3.5 12H20.33" /></>
    case 'arrange-back':
      return <><path d="M8 6V18" /><path d="M5 9L8 6L11 9" /><path d="M16 10V22" /><path d="M13 13L16 10L19 13" /></>
    case 'arrange-down':
      return <g transform="rotate(180 12 12)">{renderIcon('arrange-up')}</g>
    case 'arrange-front':
      return <><path d="M8 18V6" /><path d="M5 9L8 6L11 9" /><path d="M16 16V4" /><path d="M13 7L16 4L19 7" /></>
    case 'arrange-up':
      return <><path d="M12 20.5V3.67" /><path d="M18.07 9.57L12 3.5L5.93 9.57" /></>
    case 'brush':
      return <><path d="M21.81 3.94C20.27 7.78 16.41 13 13.18 15.59L11.21 17.17C10.96 17.35 10.71 17.51 10.43 17.62C10.43 17.44 10.42 17.24 10.39 17.05C10.28 16.21 9.9 15.43 9.23 14.76C8.55 14.08 7.72 13.68 6.87 13.57C6.67 13.56 6.47 13.54 6.27 13.56C6.38 13.25 6.55 12.96 6.76 12.72L8.32 10.75C10.9 7.52 16.14 3.64 19.97 2.11C20.56 1.89 21.13 2.05 21.49 2.42C21.87 2.79 22.05 3.36 21.81 3.94Z" /><path d="M10.43 17.62C10.43 18.72 10.01 19.77 9.22 20.57C8.61 21.18 7.78 21.6 6.79 21.73L4.33 22C2.99 22.15 1.84 21.01 2 19.65L2.27 17.19C2.51 15 4.34 13.6 6.28 13.56C6.48 13.55 6.69 13.56 6.88 13.57C7.73 13.68 8.56 14.07 9.24 14.76C9.91 15.43 10.29 16.21 10.4 17.05C10.41 17.24 10.43 17.43 10.43 17.62Z" /><path d="M14.24 14.47C14.24 11.86 12.12 9.74 9.51 9.74" /></>
    case 'brush-3':
      return <><path d="M10 16C10 14.34 10 13 13 13H17C20 13 20 12 20 10V8C20 6 20 5 17 5H16" /><path d="M12 2H8C7.35 3.95 7.35 6.05 8 8H12C12.65 6.05 12.65 3.95 12 2Z" /><path d="M8 2H7C5 2 4 3 4 5C4 7 5 8 7 8H8C7 6 7 4 8 2Z" /><path d="M13 2H12C13 4 13 6 12 8H13C15 8 16 7 16 5C16 3 15 2 13 2Z" /><path d="M9.5 22H10.5C12 22 12 21 12 20.5V17.5C12 17 12 16 10.5 16H9.5C8 16 8 17 8 17.5V20.5C8 21 8 22 9.5 22Z" /></>
    case 'eraser':
      return <><path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" /><path d="M6.99 15.08L8.93 17.02C9.57 17.66 10.63 17.66 11.27 17.02L17.02 11.27C17.66 10.63 17.66 9.57 17.02 8.93L15.08 6.99C14.44 6.35 13.38 6.35 12.74 6.99L6.99 12.74C6.34 13.38 6.34 14.43 6.99 15.08Z" /><path d="M9.31 10.42L13.58 14.69" /></>
    case 'maximize':
      return <><path d="M21 9V3H15" /><path d="M3 15V21H9" /><path d="M21 3L13.5 10.5" /><path d="M10.5 13.5L3 21" /></>
    case 'minimize':
      return <><path d="M9 3H3V9" /><path d="M15 21H21V15" /><path d="M3 3L10.5 10.5" /><path d="M13.5 13.5L21 21" /></>
    case 'mouse':
      return <><path d="M12 22C16.13 22 19.5 18.63 19.5 14.5V9.5C19.5 5.37 16.13 2 12 2C7.87 2 4.5 5.37 4.5 9.5V14.5C4.5 18.63 7.87 22 12 22Z" /><path d="M12 11C11.17 11 10.5 10.33 10.5 9.5V7.5C10.5 6.67 11.17 6 12 6C12.82 6 13.5 6.67 13.5 7.5V9.5C13.5 10.33 12.82 11 12 11Z" /><path d="M12 6V2" /></>
    case 'note':
      return <><path d="M5 3.5H15.5L19 7V20.5H5C4.45 20.5 4 20.05 4 19.5V4.5C4 3.95 4.45 3.5 5 3.5Z" /><path d="M15 3.5V7.5H19" /><path d="M7.5 11H15.5" /><path d="M7.5 15H15.5" /></>
    case 'redo':
      return <g transform="translate(24 0) scale(-1 1)">{renderIcon('undo')}</g>
    case 'search-zoom-in':
      return <><path d="M9.2 11.7H14.2" /><path d="M11.7 14.2V9.2" /><path d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.2533 16.7467 2 11.5 2C6.2533 2 2 6.2533 2 11.5C2 16.7467 6.2533 21 11.5 21Z" /><path d="M22 22L20 20" /></>
    case 'search-zoom-out':
      return <><path d="M9.2 11.7H14.2" /><path d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.2533 16.7467 2 11.5 2C6.2533 2 2 6.2533 2 11.5C2 16.7467 6.2533 21 11.5 21Z" /><path d="M22 22L20 20" /></>
    case 'text':
      return <><path d="M2.67 7.17V5.35C2.67 4.2 3.6 3.28 4.74 3.28H19.26C20.41 3.28 21.33 4.21 21.33 5.35V7.17" /><path d="M12 20.72V4.11" /><path d="M8.06 20.72H15.94" /></>
    case 'trash':
      return <><path d="M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98" /><path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" /><path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" /><path d="M10.33 16.5H13.66" /><path d="M9.5 12.5H14.5" /></>
    case 'undo':
      return <><path d="M7.13 18.31H15.13C17.89 18.31 20.13 16.07 20.13 13.31C20.13 10.55 17.89 8.31 15.13 8.31H4.13" /><path d="M6.43 10.81L3.87 8.25L6.43 5.69" /></>
    default:
      return null
  }
}
